import { http, type HttpOptions } from './httpClient'
import type { ParsedSqlModel } from '@/services/sqlImportService'
import type {
  CreateProjectRequest,
  Paginated,
  Project,
  ProjectListItem,
  UpdateProjectRequest,
} from './types'

export interface ProjectListParams {
  limit?: number
  offset?: number
  name?: string
  ordering?: string
  expand?: string
}

function normalizeProject<T extends Project | ProjectListItem>(project: T): T {
  return {
    ...project,
    current_commit_id:
      project.current_commit_id ??
      (typeof (project as { current_commit?: unknown }).current_commit === 'number'
        ? ((project as { current_commit?: number }).current_commit ?? null)
        : null),
  }
}

export const projectApi = {
  list: (workspaceId: number, params: ProjectListParams = {}) =>
    http
      .get<Paginated<ProjectListItem>>(`/api/workspaces/${workspaceId}/projects/`, { query: params })
      .then((page) => ({ ...page, results: page.results.map(normalizeProject) })),

  get: (projectId: number, expand?: 'current_commit') =>
    http
      .get<Project>(`/api/projects/${projectId}/`, {
        query: expand ? { expand } : undefined,
      })
      .then(normalizeProject),

  create: (workspaceId: number, body: CreateProjectRequest, opts?: HttpOptions) =>
    http
      .post<Project, CreateProjectRequest>(`/api/workspaces/${workspaceId}/projects/`, body, opts)
      .then(normalizeProject),

  patch: (projectId: number, body: UpdateProjectRequest, opts?: HttpOptions) =>
    http
      .patch<Project, UpdateProjectRequest>(`/api/projects/${projectId}/`, body, opts)
      .then(normalizeProject),

  delete: (projectId: number, opts?: HttpOptions) =>
    http.delete<void>(`/api/projects/${projectId}/`, opts),

  transfer: (projectId: number, targetWorkspaceId: number, opts?: HttpOptions) =>
    http
      .post<Project, { target_workspace_id: number }>(
        `/api/projects/${projectId}/transfer/`,
        { target_workspace_id: targetWorkspaceId },
        { silent: true, ...opts },
      )
      .then(normalizeProject),

  /** Server-generated SQL for the project's fixed dialect. `source` selects draft vs a commit. */
  exportSql: (
    projectId: number,
    params: { source: 'draft' | 'commit'; commitId?: number | null },
  ) =>
    http
      .get<Response>(`/api/projects/${projectId}/export/sql/`, {
        query: { source: params.source, commit_id: params.commitId ?? undefined },
        raw: true,
      })
      .then((response) => response.blob()),

  /** Server-generated data-dictionary Markdown. */
  exportDictionary: (
    projectId: number,
    params: { source: 'draft' | 'commit'; commitId?: number | null; lang?: string },
  ) =>
    http
      .get<Response>(`/api/projects/${projectId}/export/dictionary/`, {
        query: { source: params.source, commit_id: params.commitId ?? undefined, lang: params.lang },
        raw: true,
      })
      .then((response) => response.blob()),

  /** Server-generated version-comparison report (PDF) between two commits. */
  versionReport: (
    projectId: number,
    params: { baseCommitId: number; targetCommitId: number; lang?: string },
  ) =>
    http
      .get<Response>(`/api/projects/${projectId}/reports/version-diff/`, {
        query: { base_commit_id: params.baseCommitId, target_commit_id: params.targetCommitId, lang: params.lang },
        raw: true,
      })
      .then((response) => response.blob()),

  /** Parse SQL into a model within an open project — the dialect is forced to the project's. */
  importSqlParse: (projectId: number, body: { sql: string }, opts?: HttpOptions) =>
    http.post<ParsedSqlModel, { sql: string }>(`/api/projects/${projectId}/import/sql/`, body, opts),

  /** Stateless SQL parse for the new-project flow — caller supplies the dialect. */
  parseSql: (workspaceId: number, body: { sql: string; dialect: string }, opts?: HttpOptions) =>
    http.post<ParsedSqlModel, { sql: string; dialect: string }>(
      `/api/workspaces/${workspaceId}/projects/parse-sql/`,
      body,
      opts,
    ),
}
